/*
🤖 AI-Powered Analytics & Reporting System
GetMyHive Operations Center - Akıllı Rapor Üretim Sistemi
*/

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Input,
  Select,
  DatePicker,
  Table,
  Progress,
  Tag,
  Modal,
  Form,
  Tabs,
  Timeline,
  List,
  Avatar,
  Badge,
  Spin,
  Alert,
  Divider,
  Tooltip,
  Upload,
  Switch,
  Slider,
  Radio,
  Checkbox,
  Empty
} from 'antd';
import {
  BarChartOutlined,
  RobotOutlined,
  FileTextOutlined,
  DownloadOutlined,
  SearchOutlined,
  FilterOutlined,
  CalendarOutlined,
  ExportOutlined,
  EyeOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SendOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  BulbOutlined,
  TrophyOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
  DashboardOutlined,
  LineChartOutlined,
  PieChartOutlined,
  AreaChartOutlined,
  FundOutlined,
  BulbOutlined,
  ExperimentOutlined,
  CloudOutlined,
  ApiOutlined,
  DatabaseOutlined,
  ReloadOutlined,
  StarOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

// Mock AI Analysis Data
const aiInsights = [
  {
    id: '1',
    type: 'trend_analysis',
    title: 'Güvenlik Olayları Trend Analizi',
    insight: 'Son 7 günde Kadıköy bölgesinde %23 artış tespit edildi. Pattern: Akşam saatleri (18:00-21:00) yoğunlaşma.',
    confidence: 94,
    impact: 'high',
    recommendation: 'Kadıköy bölgesine akşam saatlerinde ek güvenlik tedbirleri önerilir.',
    dataPoints: 1247,
    generated: '2024-01-16 16:30'
  },
  {
    id: '2',
    type: 'anomaly_detection',
    title: 'Telefon Tacizi Anomali Tespiti',
    insight: 'Şişli bölgesinde normal pattern dışında %67 artış. Potansiyel koordineli saldırı indicator.',
    confidence: 87,
    impact: 'critical',
    recommendation: 'Acil pattern analizi ve kaynak tahsisi gerekli.',
    dataPoints: 892,
    generated: '2024-01-16 15:45'
  },
  {
    id: '3',
    type: 'performance_optimization',
    title: 'Sistem Performans Optimizasyonu',
    insight: 'AI model response time %12 iyileştirme potansiyeli. Database query optimization önerilir.',
    confidence: 91,
    impact: 'medium',
    recommendation: 'Indexes ve cache stratejilerini güncelleyin.',
    dataPoints: 2156,
    generated: '2024-01-16 14:20'
  }
];

// Mock Report Templates
const reportTemplates = [
  {
    id: '1',
    name: 'Haftalık Güvenlik Raporu',
    description: 'Güvenlik olayları, trend analizi ve öneriler',
    category: 'security',
    estimatedTime: '3-5 dakika',
    aiComplexity: 'medium',
    fields: ['threats', 'locations', 'timeline', 'recommendations']
  },
  {
    id: '2',
    name: 'AI Model Performans Raporu',
    description: 'Model doğruluğu, işlem süreleri ve optimizasyon önerileri',
    category: 'ai_performance',
    estimatedTime: '2-3 dakika',
    aiComplexity: 'high',
    fields: ['accuracy', 'response_time', 'predictions', 'optimization']
  },
  {
    id: '3',
    name: 'Operasyonel Verimlilik Raporu',
    description: 'Sistem kullanımı, resource utilization ve iyileştirme alanları',
    category: 'operations',
    estimatedTime: '4-6 dakika',
    aiComplexity: 'medium',
    fields: ['system_usage', 'resources', 'efficiency', 'recommendations']
  },
  {
    id: '4',
    name: 'Tehdit Intelligence Özeti',
    description: 'Güncel tehditler, risk seviyeleri ve counter-measure önerileri',
    category: 'threat_intelligence',
    estimatedTime: '5-7 dakika',
    aiComplexity: 'high',
    fields: ['threats', 'risk_levels', 'countermeasures', 'intelligence']
  }
];

// Mock Generated Reports
const generatedReports = [
  {
    id: '1',
    title: 'Haftalık Güvenlik Raporu - 16 Ocak 2024',
    template: 'Haftalık Güvenlik Raporu',
    status: 'completed',
    generatedBy: 'AI Assistant',
    generatedAt: '2024-01-16 16:45',
    fileSize: '2.4 MB',
    pages: 12,
    aiConfidence: 96,
    executive_summary: 'Bu hafta toplam 247 güvenlik olayı tespit edildi. En yüksek risk bölgesi Kadıköy (%23 artış).',
    insights: 8,
    recommendations: 5
  },
  {
    id: '2',
    title: 'AI Model Performans - Ocak 2024',
    template: 'AI Model Performans Raporu',
    status: 'completed',
    generatedBy: 'AI Assistant',
    generatedAt: '2024-01-16 14:30',
    fileSize: '1.8 MB',
    pages: 8,
    aiConfidence: 93,
    executive_summary: 'Model doğruluğu %94.2, ortalama response time 1.3s. Optimization potential identified.',
    insights: 6,
    recommendations: 4
  },
  {
    id: '3',
    title: 'Tehdit Intelligence - Günlük Özet',
    template: 'Tehdit Intelligence Özeti',
    status: 'generating',
    generatedBy: 'AI Assistant',
    generatedAt: '2024-01-16 16:50',
    fileSize: null,
    pages: null,
    aiConfidence: null,
    executive_summary: 'Rapor hazırlanıyor...',
    insights: null,
    recommendations: null
  }
];

const AnalyticsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [reports, setReports] = useState(generatedReports);
  const [insights, setInsights] = useState(aiInsights);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [reportGenerationModal, setReportGenerationModal] = useState(false);
  const [reportDetailModal, setReportDetailModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [insightDetailModal, setInsightDetailModal] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState<any>(null);
  const [form] = Form.useForm();

  // Statistics
  const [stats, setStats] = useState({
    totalReports: 156,
    aiGenerated: 142,
    avgConfidence: 94.2,
    processingTime: 4.8
  });

  useEffect(() => {
    // Initialize data
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Analytics data loaded');
    } catch (error) {
      console.error('Analytics loading error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 90) return '#52c41a';
    if (confidence >= 80) return '#fa8c16';
    if (confidence >= 70) return '#fadb14';
    return '#ff4d4f';
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'critical': return '#ff4d4f';
      case 'high': return '#fa8c16';
      case 'medium': return '#fadb14';
      case 'low': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge status="success" text="Tamamlandı" />;
      case 'generating':
        return <Badge status="processing" text="Hazırlanıyor" />;
      case 'failed':
        return <Badge status="error" text="Hata" />;
      case 'queued':
        return <Badge status="default" text="Sırada" />;
      default:
        return <Badge status="default" text="Bilinmiyor" />;
    }
  };

  const generateReport = async (values: any) => {
    setLoading(true);
    try {
      console.log('Generating report with:', values);
      
      // Simulate AI report generation
      const newReport = {
        id: Date.now().toString(),
        title: `${selectedTemplate.name} - ${new Date().toLocaleDateString('tr-TR')}`,
        template: selectedTemplate.name,
        status: 'generating',
        generatedBy: 'AI Assistant',
        generatedAt: new Date().toLocaleString('tr-TR'),
        fileSize: null,
        pages: null,
        aiConfidence: null,
        executive_summary: 'Rapor hazırlanıyor...',
        insights: null,
        recommendations: null
      };

      setReports(prev => [newReport, ...prev]);
      setReportGenerationModal(false);
      form.resetFields();

      // Simulate completion after 3 seconds
      setTimeout(() => {
        setReports(prev => 
          prev.map(report => 
            report.id === newReport.id 
              ? {
                  ...report,
                  status: 'completed',
                  fileSize: '2.1 MB',
                  pages: Math.floor(Math.random() * 15) + 5,
                  aiConfidence: Math.floor(Math.random() * 20) + 80,
                  executive_summary: `AI tarafından oluşturulan ${selectedTemplate.name} raporu başarıyla tamamlandı.`,
                  insights: Math.floor(Math.random() * 10) + 3,
                  recommendations: Math.floor(Math.random() * 8) + 2
                }
              : report
          )
        );
      }, 3000);

    } catch (error) {
      console.error('Report generation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openReportGeneration = (template: any) => {
    setSelectedTemplate(template);
    setReportGenerationModal(true);
  };

  const openReportDetail = (report: any) => {
    setSelectedReport(report);
    setReportDetailModal(true);
  };

  // Dashboard Tab
  const renderDashboard = () => (
    <div>
      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Rapor"
              value={stats.totalReports}
              prefix={<FileTextOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="AI Üretimi"
              value={stats.aiGenerated}
              prefix={<RobotOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Ortalama Güvenilirlik"
              value={stats.avgConfidence}
              suffix="%"
              prefix={<TrophyOutlined style={{ color: '#fa8c16' }} />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Ortalama Süre"
              value={stats.processingTime}
              suffix=" dk"
              prefix={<ClockCircleOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* AI Insights */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card 
            title={
              <Space>
                <BulbOutlined />
                <span>AI İçgörüleri</span>
              </Space>
            }
            extra={
              <Button 
                icon={<ReloadOutlined />}
                onClick={loadAnalyticsData}
                loading={loading}
              >
                Yenile
              </Button>
            }
          >
            <List
              dataSource={insights}
              renderItem={(insight) => (
                <List.Item
                  actions={[
                    <Button 
                      size="small" 
                      icon={<EyeOutlined />}
                      onClick={() => {
                        setSelectedInsight(insight);
                        setInsightDetailModal(true);
                      }}
                    >
                      Detay
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar 
                        icon={<BulbOutlined />}
                        style={{ backgroundColor: getConfidenceColor(insight.confidence) }}
                      />
                    }
                    title={
                      <Space>
                        <span>{insight.title}</span>
                        <Tag color={getImpactColor(insight.impact)}>
                          {insight.impact.toUpperCase()}
                        </Tag>
                        <Tag color="blue">
                          %{insight.confidence} güvenilir
                        </Tag>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size="small">
                        <Text>{insight.insight}</Text>
                        <Text type="secondary">
                          <strong>Öneri:</strong> {insight.recommendation}
                        </Text>
                        <Text type="secondary">
                          {insight.dataPoints} veri noktası • {insight.generated}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card 
            title={
              <Space>
                <BarChartOutlined />
                <span>Sistem Metrikleri</span>
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Text type="secondary">AI Model Doğruluğu</Text>
                <Progress percent={94} strokeColor="#52c41a" />
              </div>
              <div>
                <Text type="secondary">Rapor Üretim Hızı</Text>
                <Progress percent={87} strokeColor="#1890ff" />
              </div>
              <div>
                <Text type="secondary">Veri İşleme Kapasitesi</Text>
                <Progress percent={76} strokeColor="#fa8c16" />
              </div>
              <div>
                <Text type="secondary">Sistem Sağlığı</Text>
                <Progress percent={98} strokeColor="#722ed1" />
              </div>
            </Space>
          </Card>

          <Card 
            title={
              <Space>
                <ThunderboltOutlined />
                <span>Hızlı İşlemler</span>
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                block
                onClick={() => openReportGeneration(reportTemplates[0])}
              >
                Acil Güvenlik Raporu
              </Button>
              <Button 
                icon={<RobotOutlined />} 
                block
                onClick={() => openReportGeneration(reportTemplates[1])}
              >
                AI Performans Analizi
              </Button>
              <Button 
                icon={<WarningOutlined />} 
                block
                onClick={() => openReportGeneration(reportTemplates[3])}
              >
                Tehdit Intelligence
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );

  // Report Templates Tab
  const renderTemplates = () => (
    <Row gutter={[16, 16]}>
      {reportTemplates.map((template) => (
        <Col xs={24} md={12} lg={8} key={template.id}>
          <Card
            hoverable
            actions={[
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => openReportGeneration(template)}
              >
                Rapor Oluştur
              </Button>
            ]}
          >
            <Card.Meta
              avatar={
                <Avatar 
                  icon={<FileTextOutlined />}
                  style={{ backgroundColor: '#1890ff' }}
                />
              }
              title={template.name}
              description={
                <Space direction="vertical" size="small">
                  <Text type="secondary">{template.description}</Text>
                  <Space wrap>
                    <Tag color="blue">{template.category}</Tag>
                    <Tag color="green">{template.estimatedTime}</Tag>
                    <Tag color="orange">{template.aiComplexity}</Tag>
                  </Space>
                  <Text type="secondary">
                    <strong>Alanlar:</strong> {template.fields.join(', ')}
                  </Text>
                </Space>
              }
            />
          </Card>
        </Col>
      ))}
    </Row>
  );

  // Generated Reports Tab
  const renderReports = () => (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Input.Search
            placeholder="Rapor ara..."
            style={{ width: 300 }}
            prefix={<SearchOutlined />}
          />
          <Select defaultValue="all" style={{ width: 150 }}>
            <Option value="all">Tüm Durumlar</Option>
            <Option value="completed">Tamamlanan</Option>
            <Option value="generating">Hazırlanan</Option>
          </Select>
          <RangePicker />
        </Space>
      </div>

      <List
        dataSource={reports}
        renderItem={(report) => (
          <List.Item
            actions={[
              <Button 
                size="small" 
                icon={<EyeOutlined />}
                onClick={() => openReportDetail(report)}
              >
                Görüntüle
              </Button>,
              <Button 
                size="small" 
                icon={<DownloadOutlined />}
                disabled={report.status !== 'completed'}
              >
                İndir
              </Button>,
              <Button 
                size="small" 
                icon={<DeleteOutlined />}
                danger
              >
                Sil
              </Button>
            ]}
          >
            <List.Item.Meta
              avatar={
                <Avatar 
                  icon={<FileTextOutlined />}
                  style={{ 
                    backgroundColor: report.status === 'completed' ? '#52c41a' : '#fa8c16'
                  }}
                />
              }
              title={
                <Space>
                  <span>{report.title}</span>
                  {getStatusBadge(report.status)}
                  {report.aiConfidence && (
                    <Tag color="blue">%{report.aiConfidence} AI güvenilir</Tag>
                  )}
                </Space>
              }
              description={
                <Space direction="vertical" size="small">
                  <Text type="secondary">
                    <RobotOutlined /> {report.generatedBy} • {report.generatedAt}
                  </Text>
                  {report.fileSize && (
                    <Text type="secondary">
                      📄 {report.pages} sayfa • 💾 {report.fileSize}
                    </Text>
                  )}
                  <Text>{report.executive_summary}</Text>
                  {report.insights && (
                    <Space>
                      <Tag color="blue">{report.insights} içgörü</Tag>
                      <Tag color="green">{report.recommendations} öneri</Tag>
                    </Space>
                  )}
                </Space>
              }
            />
          </List.Item>
        )}
      />
    </div>
  );

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <Space>
            <RobotOutlined style={{ color: '#1890ff' }} />
            🤖 AI-Powered Analytics & Reporting
          </Space>
        </Title>
        <Paragraph type="secondary">
          Yapay zeka destekli analitik ve rapor üretim sistemi. Operations Center verilerini analiz eder, 
          trendleri tespit eder ve akıllı raporlar üretir.
        </Paragraph>
      </div>

      {/* Main Content */}
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane 
          tab={
            <Space>
              <DashboardOutlined />
              Dashboard
            </Space>
          } 
          key="dashboard"
        >
          {renderDashboard()}
        </TabPane>

        <TabPane 
          tab={
            <Space>
              <FileTextOutlined />
              Rapor Şablonları
            </Space>
          } 
          key="templates"
        >
          {renderTemplates()}
        </TabPane>

        <TabPane 
          tab={
            <Space>
              <BarChartOutlined />
              Üretilen Raporlar
            </Space>
          } 
          key="reports"
        >
          {renderReports()}
        </TabPane>

        <TabPane 
          tab={
            <Space>
              <SettingOutlined />
              AI Ayarları
            </Space>
          } 
          key="settings"
        >
          <Card title="AI Model Konfigürasyonu">
            <Form layout="vertical">
              <Form.Item label="Model Doğruluk Eşiği">
                <Slider defaultValue={85} min={70} max={99} marks={{ 70: '70%', 85: '85%', 99: '99%' }} />
              </Form.Item>
              <Form.Item label="Rapor Detay Seviyesi">
                <Radio.Group defaultValue="detailed">
                  <Radio value="summary">Özet</Radio>
                  <Radio value="detailed">Detaylı</Radio>
                  <Radio value="comprehensive">Kapsamlı</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item label="Otomatik İçgörü Üretimi">
                <Switch defaultChecked /> AI tarafından otomatik içgörü üretimi
              </Form.Item>
            </Form>
          </Card>
        </TabPane>
      </Tabs>

      {/* Report Generation Modal */}
      <Modal
        title={`🤖 AI Rapor Üretimi: ${selectedTemplate?.name}`}
        open={reportGenerationModal}
        onCancel={() => setReportGenerationModal(false)}
        footer={null}
        width={700}
      >
        {selectedTemplate && (
          <div>
            <Alert
              message="AI Destekli Rapor Üretimi"
              description={`Bu rapor yapay zeka tarafından ${selectedTemplate.estimatedTime} içinde hazırlanacaktır.`}
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
            
            <Form
              form={form}
              layout="vertical"
              onFinish={generateReport}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Rapor Başlığı"
                    name="title"
                    initialValue={`${selectedTemplate.name} - ${new Date().toLocaleDateString('tr-TR')}`}
                    rules={[{ required: true, message: 'Başlık gerekli' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Tarih Aralığı"
                    name="dateRange"
                    rules={[{ required: true, message: 'Tarih aralığı gerekli' }]}
                  >
                    <RangePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Rapor Kapsamı"
                name="scope"
                initialValue="comprehensive"
              >
                <Radio.Group>
                  <Radio value="summary">Özet Rapor</Radio>
                  <Radio value="detailed">Detaylı Analiz</Radio>
                  <Radio value="comprehensive">Kapsamlı İnceleme</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                label="Dahil Edilecek Bölümler"
                name="sections"
                initialValue={selectedTemplate.fields}
              >
                <Checkbox.Group>
                  {selectedTemplate.fields.map((field: string) => (
                    <Checkbox key={field} value={field}>
                      {field.replace('_', ' ').toUpperCase()}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </Form.Item>

              <Form.Item
                label="AI Konfigürasyonu"
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text>Detay Seviyesi:</Text>
                    <Slider
                      defaultValue={3}
                      min={1}
                      max={5}
                      marks={{ 1: 'Basit', 3: 'Orta', 5: 'Detaylı' }}
                      style={{ marginTop: 8 }}
                    />
                  </div>
                  <Switch defaultChecked /> Görsel analizler dahil et
                  <Switch defaultChecked /> Tahmin ve projeksiyonlar ekle
                  <Switch /> Executive summary oluştur
                </Space>
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={loading}
                    icon={<RobotOutlined />}
                  >
                    AI ile Rapor Üret
                  </Button>
                  <Button onClick={() => setReportGenerationModal(false)}>
                    İptal
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* Report Detail Modal */}
      <Modal
        title="📊 Rapor Detayları"
        open={reportDetailModal}
        onCancel={() => setReportDetailModal(false)}
        footer={[
          <Button key="download" type="primary" icon={<DownloadOutlined />}>
            Raporu İndir
          </Button>,
          <Button key="close" onClick={() => setReportDetailModal(false)}>
            Kapat
          </Button>
        ]}
        width={800}
      >
        {selectedReport && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={16}>
                <Space direction="vertical" size="small">
                  <Title level={4}>{selectedReport.title}</Title>
                  <Text type="secondary">
                    {selectedReport.generatedBy} tarafından {selectedReport.generatedAt} tarihinde oluşturuldu
                  </Text>
                  {getStatusBadge(selectedReport.status)}
                </Space>
              </Col>
              <Col span={8}>
                {selectedReport.aiConfidence && (
                  <Statistic
                    title="AI Güvenilirlik"
                    value={selectedReport.aiConfidence}
                    suffix="%"
                    valueStyle={{ color: getConfidenceColor(selectedReport.aiConfidence) }}
                  />
                )}
              </Col>
            </Row>

            <Divider />

            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Statistic title="Sayfa Sayısı" value={selectedReport.pages || 0} />
              </Col>
              <Col span={8}>
                <Statistic title="Dosya Boyutu" value={selectedReport.fileSize || 'N/A'} />
              </Col>
              <Col span={8}>
                <Statistic title="İçgörü Sayısı" value={selectedReport.insights || 0} />
              </Col>
            </Row>

            <Divider />

            <div>
              <Title level={5}>Executive Summary</Title>
              <Paragraph>{selectedReport.executive_summary}</Paragraph>
            </div>

            {selectedReport.status === 'completed' && (
              <div>
                <Title level={5}>Rapor İçeriği Önizlemesi</Title>
                <Timeline>
                  <Timeline.Item color="green">
                    <strong>Veri Toplama:</strong> {Math.floor(Math.random() * 10000)} kayıt işlendi
                  </Timeline.Item>
                  <Timeline.Item color="blue">
                    <strong>AI Analiz:</strong> Pattern tespiti ve trend analizi tamamlandı
                  </Timeline.Item>
                  <Timeline.Item color="orange">
                    <strong>İçgörü Üretimi:</strong> {selectedReport.insights} adet içgörü oluşturuldu
                  </Timeline.Item>
                  <Timeline.Item color="purple">
                    <strong>Öneri Geliştirme:</strong> {selectedReport.recommendations} adet aksiyon önerisi
                  </Timeline.Item>
                </Timeline>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* AI Insight Detail Modal */}
      <Modal
        title="💡 AI İçgörü Detayları"
        open={insightDetailModal}
        onCancel={() => setInsightDetailModal(false)}
        footer={[
          <Button key="close" onClick={() => setInsightDetailModal(false)}>
            Kapat
          </Button>
        ]}
        width={700}
      >
        {selectedInsight && (
          <div>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Header */}
              <div>
                <Space>
                  <Avatar 
                    icon={<BulbOutlined />}
                    style={{ backgroundColor: getConfidenceColor(selectedInsight.confidence) }}
                    size="large"
                  />
                  <div>
                    <Title level={4} style={{ margin: 0 }}>
                      {selectedInsight.title}
                    </Title>
                    <Space>
                      <Tag color={getImpactColor(selectedInsight.impact)}>
                        {selectedInsight.impact.toUpperCase()}
                      </Tag>
                      <Tag color="blue">
                        %{selectedInsight.confidence} güvenilir
                      </Tag>
                      <Tag color="green">
                        {selectedInsight.type.replace('_', ' ').toUpperCase()}
                      </Tag>
                    </Space>
                  </div>
                </Space>
              </div>

              {/* Insight Content */}
              <Card size="small" style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text strong style={{ color: '#389e0d' }}>🔍 AI İçgörüsü:</Text>
                  <Paragraph style={{ margin: 0, fontSize: '14px' }}>
                    {selectedInsight.insight}
                  </Paragraph>
                </Space>
              </Card>

              {/* Recommendation */}
              <Card size="small" style={{ backgroundColor: '#fff7e6', border: '1px solid #ffd591' }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text strong style={{ color: '#d46b08' }}>💡 Öneriler:</Text>
                  <Paragraph style={{ margin: 0, fontSize: '14px' }}>
                    {selectedInsight.recommendation}
                  </Paragraph>
                </Space>
              </Card>

              {/* Metadata */}
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic
                    title="Güvenilirlik Skoru"
                    value={selectedInsight.confidence}
                    suffix="%"
                    valueStyle={{ color: getConfidenceColor(selectedInsight.confidence) }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Veri Noktası"
                    value={selectedInsight.dataPoints}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Impact Seviyesi"
                    value={selectedInsight.impact}
                    valueStyle={{ color: getImpactColor(selectedInsight.impact) }}
                  />
                </Col>
              </Row>

              {/* Analysis Details */}
              <Card title="📊 Analiz Detayları" size="small">
                <Timeline>
                  <Timeline.Item color="blue">
                    <Text strong>Veri Toplama:</Text> {selectedInsight.dataPoints} kayıt analiz edildi
                  </Timeline.Item>
                  <Timeline.Item color="green">
                    <Text strong>Pattern Tespiti:</Text> {selectedInsight.type.replace('_', ' ')} algoritması kullanıldı
                  </Timeline.Item>
                  <Timeline.Item color="orange">
                    <Text strong>Güvenilirlik Hesaplama:</Text> %{selectedInsight.confidence} doğruluk oranı
                  </Timeline.Item>
                  <Timeline.Item color="purple">
                    <Text strong>Öneri Üretimi:</Text> {selectedInsight.generated} tarihinde tamamlandı
                  </Timeline.Item>
                </Timeline>
              </Card>

              {/* Technical Info */}
              <Card title="🔧 Teknik Bilgiler" size="small">
                <Row gutter={[16, 8]}>
                  <Col span={12}>
                    <Text type="secondary">Analiz Türü:</Text>
                    <br />
                    <Text strong>{selectedInsight.type.replace('_', ' ').toUpperCase()}</Text>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary">Oluşturulma:</Text>
                    <br />
                    <Text strong>{selectedInsight.generated}</Text>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary">Veri Kalitesi:</Text>
                    <br />
                    <Progress 
                      percent={Math.floor(selectedInsight.confidence * 0.9)} 
                      size="small"
                      strokeColor={getConfidenceColor(selectedInsight.confidence)}
                    />
                  </Col>
                  <Col span={12}>
                    <Text type="secondary">Aksiyon Önceliği:</Text>
                    <br />
                    <Tag color={getImpactColor(selectedInsight.impact)} style={{ marginTop: 4 }}>
                      {selectedInsight.impact === 'critical' ? 'ACİL' : 
                       selectedInsight.impact === 'high' ? 'YÜKSEK' :
                       selectedInsight.impact === 'medium' ? 'ORTA' : 'DÜŞÜK'}
                    </Tag>
                  </Col>
                </Row>
              </Card>

              {/* Action Buttons */}
              <Card size="small" style={{ backgroundColor: '#f0f2f5' }}>
                <Space wrap>
                  <Button type="primary" icon={<DownloadOutlined />}>
                    Rapor Olarak İndir
                  </Button>
                  <Button icon={<SendOutlined />}>
                    Ekibe Gönder
                  </Button>
                  <Button icon={<StarOutlined />}>
                    Favorilere Ekle
                  </Button>
                  <Button icon={<ReloadOutlined />}>
                    Yeniden Analiz Et
                  </Button>
                </Space>
              </Card>
            </Space>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AnalyticsPage;